import { useEffect, useMemo, useState } from 'react'
import type { RemoteHarnessState, RemoteHoleCards } from '../harness/remoteTypes'
import { text } from '../i18n'
import { formatChips } from '../lib/domain'
import './remote-test.css'

export type RemoteLeaderAction = 'open-street' | 'award-pot' | 'start-next-hand' | null

export interface RemoteControlPolicy {
  /** The ordinary chip composer may be used only by the current poker actor. */
  regularBettingEnabled: boolean
  /** Folding is narrower than betting because a forced blind can never fold. */
  foldEnabled: boolean
  /** Check is a durable test action, not a pot contribution. */
  checkEnabled: boolean
  /** The only table-lifecycle action the Leader may take at this point. */
  leaderAction: RemoteLeaderAction
}

/**
 * Centralizes the phone-control gate so the visual guide and App controls can
 * never disagree about whose turn it is. Normal rooms should skip this helper.
 */
export function getRemoteControlPolicy(
  state: RemoteHarnessState,
  playerUid: string,
  isLeader: boolean,
): RemoteControlPolicy {
  const ownsTurn = state.actorUid === playerUid
  const postingBlind = state.phase === 'posting_blinds'
  const betting = state.phase === 'betting'
  const legal = new Set(state.prompt.legalActions)

  let leaderAction: RemoteLeaderAction = null
  if (isLeader && state.phase === 'awaiting_street') leaderAction = 'open-street'
  else if (isLeader && state.phase === 'awaiting_award') leaderAction = 'award-pot'
  else if (isLeader && state.phase === 'awaiting_next_hand') leaderAction = 'start-next-hand'

  return {
    regularBettingEnabled: ownsTurn && (postingBlind || betting),
    foldEnabled: ownsTurn && betting && legal.has('fold'),
    checkEnabled: ownsTurn && betting && state.canCheck && legal.has('check'),
    leaderAction,
  }
}

const SUIT_SYMBOL: Readonly<Record<string, string>> = {
  c: '♣',
  d: '♦',
  h: '♥',
  s: '♠',
}

const SUIT_NAME: Readonly<Record<string, string>> = {
  c: 'clubs',
  d: 'diamonds',
  h: 'hearts',
  s: 'spades',
}

interface ParsedCard {
  rank: string
  suit: string
  symbol: string
  label: string
  red: boolean
}

export function parseRemoteCard(code: string): ParsedCard | null {
  const match = /^([2-9TJQKA])([cdhs])$/i.exec(code.trim())
  if (!match) return null
  const rank = match[1].toUpperCase()
  const suit = match[2].toLowerCase()
  return {
    rank,
    suit,
    symbol: SUIT_SYMBOL[suit],
    label: `${rank} of ${SUIT_NAME[suit]}`,
    red: suit === 'd' || suit === 'h',
  }
}

function RemoteCard({ code, hiddenLabel }: { code?: string; hiddenLabel: string }) {
  const card = code ? parseRemoteCard(code) : null
  if (!card) return <span className="remote-test-card remote-test-card--empty" aria-label={hiddenLabel}>P</span>
  return <span className={`remote-test-card ${card.red ? 'remote-test-card--red' : ''}`} aria-label={card.label}>
    <b>{card.rank}</b><i>{card.symbol}</i>
  </span>
}

function streetName(state: RemoteHarnessState): string {
  if (state.street === 'preflop') return text('PRE-FLOP', 'לפני הפלופ')
  if (state.street === 'showdown') return text('SHOWDOWN', 'חשיפה')
  return state.street.toUpperCase()
}

function roleName(state: RemoteHarnessState, playerUid: string): string | null {
  const roles: string[] = []
  if (state.dealerUid === playerUid) roles.push('D')
  if (state.smallBlindUid === playerUid) roles.push('SB')
  if (state.bigBlindUid === playerUid) roles.push('BB')
  return roles.length ? roles.join(' · ') : null
}

function roleAriaName(state: RemoteHarnessState, playerUid: string): string {
  const roles: string[] = []
  if (state.dealerUid === playerUid) roles.push(text('Dealer', 'דילר'))
  if (state.smallBlindUid === playerUid) roles.push(text('small blind', 'בליינד קטן'))
  if (state.bigBlindUid === playerUid) roles.push(text('big blind', 'בליינד גדול'))
  return roles.join(', ')
}

export interface RemoteStatusCopy {
  eyebrow: string
  title: string
  detail: string
  tone: 'turn' | 'waiting' | 'paused'
}

export function getRemoteStatusCopy(
  state: RemoteHarnessState,
  playerUid: string,
  isLeader: boolean,
  namesByUid: Readonly<Record<string, string>> = {},
): RemoteStatusCopy {
  const actorName = namesByUid[state.actorUid]
  const winnerName = state.expectedWinnerUid ? namesByUid[state.expectedWinnerUid] : undefined
  const ownsTurn = state.actorUid === playerUid

  if (state.phase === 'paused') {
    return {
      eyebrow: text('TEST PAUSED', 'הבדיקה נעצרה'),
      title: state.prompt.headline || text('Stop here', 'עוצרים כאן'),
      detail: state.prompt.detail || text('Keep this screen open and check the dealer screen.', 'משאירים את המסך פתוח ובודקים את מסך הדילר.'),
      tone: 'paused',
    }
  }

  if (state.phase === 'waiting') {
    return {
      eyebrow: text('GETTING READY', 'מתכוננים'),
      title: state.prompt.headline || text('Waiting for the Leader', 'ממתינים למוביל/ה'),
      detail: state.prompt.detail || text('Keep this screen open. Your cards will appear when the hand starts.', 'משאירים את המסך פתוח. הקלפים יופיעו כשהיד תתחיל.'),
      tone: 'waiting',
    }
  }

  if (state.phase === 'awaiting_street') {
    return isLeader ? {
      eyebrow: text('LEADER ACTION', 'פעולת מוביל/ה'),
      title: state.prompt.headline || text('Open the next street', 'פתיחת השלב הבא'),
      detail: text('Betting is settled. Use the Leader control below to reveal the next street.', 'ההימורים הסתיימו. משתמשים בבקרת המוביל/ה למטה כדי לחשוף את השלב הבא.'),
      tone: 'turn',
    } : {
      eyebrow: text('WAITING', 'ממתינים'),
      title: text('Waiting for the Leader', 'ממתינים למוביל/ה'),
      detail: text('They will open the next street. Do not add chips yet.', 'המוביל/ה יפתח/תפתח את השלב הבא. עדיין לא מוסיפים צ׳יפים.'),
      tone: 'waiting',
    }
  }

  if (state.phase === 'awaiting_award') {
    return isLeader ? {
      eyebrow: text('LEADER ACTION', 'פעולת מוביל/ה'),
      title: winnerName ? text(`Award the pot to ${winnerName}`, `הענקת הקופה ל${winnerName}`) : text('Award the pot', 'הענקת הקופה'),
      detail: text(`Award exactly ${formatChips(state.expectedPot)} using the Leader control below.`, `מעניקים בדיוק ${formatChips(state.expectedPot)} בבקרת המוביל/ה למטה.`),
      tone: 'turn',
    } : {
      eyebrow: text('HAND COMPLETE', 'היד הסתיימה'),
      title: winnerName ? text(`${winnerName} wins`, `${winnerName} זוכה`) : text('Waiting for the award', 'ממתינים להענקת הקופה'),
      detail: text('The Leader is awarding the pot. Do not add chips.', 'המוביל/ה מעניק/ה את הקופה. לא מוסיפים צ׳יפים.'),
      tone: 'waiting',
    }
  }

  if (state.phase === 'awaiting_next_hand') {
    return isLeader ? {
      eyebrow: text('LEADER ACTION', 'פעולת מוביל/ה'),
      title: text('Start the next hand', 'התחלת היד הבאה'),
      detail: text('The pot and balances matched. Use the Leader control below when both players are ready.', 'הקופה והיתרות תואמות. משתמשים בבקרת המוביל/ה למטה כששני השחקנים מוכנים.'),
      tone: 'turn',
    } : {
      eyebrow: text('HAND PASSED', 'היד עברה בהצלחה'),
      title: text('Waiting for the next hand', 'ממתינים ליד הבאה'),
      detail: text('The Leader will start it when both players are ready.', 'המוביל/ה יתחיל/תתחיל כששני השחקנים מוכנים.'),
      tone: 'waiting',
    }
  }

  if (ownsTurn) {
    const blind = state.requiredBlind?.playerUid === playerUid ? state.requiredBlind : null
    const exactDetail = blind
      ? text(`Add exactly ${formatChips(blind.amount)} to the pot.`, `מוסיפים בדיוק ${formatChips(blind.amount)} לקופה.`)
      : state.prompt.detail
    const callDetail = !blind && state.prompt.toCall > 0
      ? text(` To call: ${formatChips(state.prompt.toCall)}.`, ` להשוואה: ${formatChips(state.prompt.toCall)}.`)
      : ''
    return {
      eyebrow: text('YOUR TURN', 'התור שלך'),
      title: blind
        ? text(`Post the ${blind.kind} blind`, `הוספת הבליינד ה${blind.kind === 'small' ? 'קטן' : 'גדול'}`)
        : state.prompt.headline,
      detail: `${exactDetail}${callDetail}`,
      tone: 'turn',
    }
  }

  return {
    eyebrow: text('WAITING', 'ממתינים'),
    title: actorName ? text(`Waiting for ${actorName}`, `ממתינים ל${actorName}`) : text('The other player is acting', 'השחקן/ית השני/ה פועל/ת'),
    detail: text('Keep this screen open. Do not add chips until it says YOUR TURN.', 'משאירים את המסך פתוח. לא מוסיפים צ׳יפים עד שמופיע ״התור שלך״.'),
    tone: 'waiting',
  }
}

export interface RemoteTestPanelProps {
  state: RemoteHarnessState
  holeCards: RemoteHoleCards | null
  playerUid: string
  isLeader: boolean
  namesByUid?: Readonly<Record<string, string>>
  disabled?: boolean
  onCheck: () => Promise<void>
}

export function RemoteTestPanel({
  state,
  holeCards,
  playerUid,
  isLeader,
  namesByUid = {},
  disabled = false,
  onCheck,
}: RemoteTestPanelProps) {
  const [submittedTurnToken, setSubmittedTurnToken] = useState<string | null>(null)
  const [checkError, setCheckError] = useState<string | null>(null)
  const policy = getRemoteControlPolicy(state, playerUid, isLeader)
  const status = getRemoteStatusCopy(state, playerUid, isLeader, namesByUid)
  const cardsMatch = holeCards?.uid === playerUid
    && holeCards.runId === state.runId
    && holeCards.handNumber === state.handNumber
  const ownCards = cardsMatch ? holeCards.cards : []
  const role = roleName(state, playerUid)
  const checkPending = submittedTurnToken === state.turnToken

  useEffect(() => {
    if (submittedTurnToken && submittedTurnToken !== state.turnToken) {
      setSubmittedTurnToken(null)
      setCheckError(null)
    }
  }, [state.turnToken, submittedTurnToken])

  const board = useMemo(
    () => Array.from({ length: 5 }, (_, index) => state.board[index]),
    [state.board],
  )

  const submitCheck = async () => {
    if (!policy.checkEnabled || disabled || checkPending) return
    setCheckError(null)
    setSubmittedTurnToken(state.turnToken)
    try {
      await onCheck()
    } catch (reason) {
      setSubmittedTurnToken(null)
      setCheckError(reason instanceof Error ? reason.message : String(reason))
    }
  }

  return <section className="remote-test-panel" aria-label={text('Private test hand', 'יד בדיקה פרטית')}>
    <div className="remote-test-panel__topline">
      <span className="remote-test-panel__mode">{text('PRIVATE TEST HAND', 'יד בדיקה פרטית')}</span>
      <span className="remote-test-panel__street">{text(`HAND ${state.handNumber}`, `יד ${state.handNumber}`)} · {streetName(state)}</span>
      {role && <span className="remote-test-panel__role" aria-label={text(`Your role: ${roleAriaName(state, playerUid)}`, `התפקיד שלך: ${roleAriaName(state, playerUid)}`)}>{role}</span>}
    </div>

    <div className="remote-test-panel__table">
      <div className="remote-test-panel__hand">
        <span>{text('YOUR CARDS', 'הקלפים שלך')}</span>
        <div className="remote-test-panel__cards">
          <RemoteCard code={ownCards[0]} hiddenLabel={text('Card is being dealt', 'הקלף מחולק')}/>
          <RemoteCard code={ownCards[1]} hiddenLabel={text('Card is being dealt', 'הקלף מחולק')}/>
        </div>
      </div>
      <div className="remote-test-panel__board">
        <span>{text('BOARD', 'לוח')}</span>
        <div className="remote-test-panel__cards">
          {board.map((code, index) => <RemoteCard key={index} code={code} hiddenLabel={text('Unrevealed community card', 'קלף קהילה שטרם נחשף')}/>) }
        </div>
      </div>
    </div>

    <div className={`remote-test-panel__status remote-test-panel__status--${status.tone}`} role="status" aria-live="polite">
      <span>{status.eyebrow}</span>
      <strong>{status.title}</strong>
      <p>{status.detail}</p>
    </div>

    {policy.checkEnabled && <button className="remote-test-panel__check" disabled={disabled || checkPending} onClick={() => void submitCheck()}>
      {checkPending ? text('CHECK SENT · WAITING…', 'צ׳ק נשלח · ממתינים…') : text('CHECK', 'צ׳ק')}
    </button>}
    {checkError && <p className="remote-test-panel__error" role="alert">{checkError}</p>}
  </section>
}
